import {
  CustomIntegrationSettingsType,
  IntegrationEnum,
} from 'models/InternalIntegrationSetting';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import useFetchIntegrationAgencySettings from 'hooks/useFetchIntegrationAgencySettings';
import { HostfullyLinkContainer } from './IntegrationCell.styles';

/**
 * Maps integrations to in-app links (that don't need target="_blank").
 */
const IN_APP_ACCESS_LINKS: Partial<Record<IntegrationEnum, string>> = {
  [IntegrationEnum.HOSTFULLYASSIST]: '/inbox',
};

/** Access link for the products whose URL does not expire. */
const StaticAccessLink = ({
  integrationId,
}: {
  integrationId: IntegrationEnum;
}) => {
  const { t } = useTranslation();

  const inAppLink = IN_APP_ACCESS_LINKS[integrationId];

  const { data: integrationSettings, isLoading } =
    useFetchIntegrationAgencySettings({
      enumId: integrationId,
      enabled: !inAppLink,
    });

  const getAccessLink = () => {
    const { agencySettings } = integrationSettings ?? {};

    switch (agencySettings?.customIntegrationSettings?.type) {
      case CustomIntegrationSettingsType.HOSTFULLY_CAPITAL:
      case CustomIntegrationSettingsType.HOSTFULLY_DEVICES:
        return agencySettings?.customIntegrationSettings?.accessUrl ?? '';
      default:
        return '';
    }
  };

  const linkLabelMap = {
    [IntegrationEnum.HOSTFULLYCAPITAL]: t(
      'pageIntegrations.table.hostfullyCapital',
    ),
    [IntegrationEnum.HOSTFULLYDEVICES]: t(
      'pageIntegrations.table.devicesAdmin',
    ),
    [IntegrationEnum.HOSTFULLYASSIST]: t(
      'pageIntegrations.table.hostfullyAssist',
    ),
  };

  const label = linkLabelMap[integrationId as keyof typeof linkLabelMap];
  const link = inAppLink ?? getAccessLink();

  if (!link || (!inAppLink && isLoading)) {
    return null;
  }

  return (
    <HostfullyLinkContainer>
      <Trans
        i18nKey="pageIntegrations.table.accessLink"
        values={{ label }}
        components={{
          1: (
            <Link
              to={link}
              onClick={(e) => e.stopPropagation()}
              {...(!inAppLink && { target: '_blank', rel: 'noreferrer' })}
              aria-label="Access link"
              data-testid="access-link-hostfully-integration"
            />
          ),
        }}
      />
    </HostfullyLinkContainer>
  );
};

export default StaticAccessLink;
